import { useQuery } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "./api";

// ============================================================================
// New BOM types (BOM Plan II-A)
// ============================================================================

export interface BomListItem {
  bomId: number;
  name: string | null;
  lifecycleStatus: string;
  lineItemCount: number;
}

export interface BomEvent {
  bomEventId: number;
  bomId: number;
  jobId: number | null;
  description: string;
  createdOn: string;
  createdByName: string | null;
}

export interface BomAdminListItem {
  bomId: number;
  displayProductName: string | null;
  lifecycleStatus: string;
  lineItemCount: number;
  sourceProductId: number | null;
  parentType: string | null;
  parentId: number | null;
}

export interface BomAdminListPage {
  items: BomAdminListItem[];
  total: number;
}

export interface BomLineItem {
  bomLineItemId: number;
  bomId: number;
  bomItemId: number | null;
  itemName: string;
  calcType: string;
  calcFactor: number | null;
  sortOrder: number;
  supplierId: number | null;
  supplierName: string | null;
  unitCost: number | null;
  unitCurrencyCode: string;
  priceByRange: boolean;
  unitCostByRangeJson: string | null;
  quantityValues: string | null;
  itemiseOnQuote: boolean;
  note: string | null;
  priceTableId: number | null;
  rateTypeCode: string | null;
  rateAxis: string;
  rateColumn: number | null;
  rowVersion: string | null;
}

export interface BomDetail {
  bomId: number;
  bomTemplateId: number | null;
  displayProductName: string | null;
  sourceProductId: number | null;
  decoMethodCode: string | null;
  colours: number | null;
  positions: number | null;
  currencyCode: string;
  currencyExchangeRate: number | null;
  quantityColsJson: string | null;
  quoteNote: string | null;
  lifecycleStatus: string;
  lockedReason: string | null;
  rowVersion: string | null;
  items: BomLineItem[];
}

export interface PricingLineBreakdown {
  bomLineItemId: number | null;
  itemName: string;
  calcType: string;
  lineValueAud: number;
}

export interface PricingColumn {
  quantity: number;
  overs: number;
  totalSheets: number;
  totalQuantity: number;
  totalCostAud: number;
  unitCostAud: number;
  markupPct: number;
  adjustAmt: number;
  profit: number;
  unitPriceAud: number;
  totalPriceAud: number;
  profitWarning: boolean;
  lineBreakdown: PricingLineBreakdown[];
}

export interface PricingResult {
  bomId: number;
  currencyCode: string;
  exchangeRate: number | null;
  columns: PricingColumn[];
}

export interface PriceBomBody {
  quantities: number[];
  overs?: number[];
  markupPcts?: number[];
  adjustAmts?: number[];
}

// ============================================================================
// Legacy types (kept for BomPreviewTable compatibility — Plan I)
// ============================================================================

export interface BomLineItemPreviewDto {
  id: number;
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface BomPreviewDto {
  bomId: number | null;
  items: BomLineItemPreviewDto[];
}

/** Legacy BomDetailDto — kept for backward compatibility with BomPreviewTable.
 *  The new, richer type is `BomDetail` above. */
export interface BomDetailDto {
  bomId: number;
  displayProductName: string | null;
  lifecycleStatus: string;
  items: ReadonlyArray<{
    bomLineItemId: number;
    bomId: number;
    itemName: string;
    sortOrder: number;
    unitCost: number | null;
    quantityValues: string | null;
  }>;
}

// ============================================================================
// Query keys
// ============================================================================

export const bomKeys = {
  byParent: (parentType: "Quote" | "Job", parentId: number) =>
    ["boms", parentType, parentId] as const,
  detail: (bomId: number) => ["bom", bomId] as const,
  /** Legacy — kept for useJobBomPreview */
  jobPreview: (jobId: number) => ["jobs", jobId, "bom-preview"] as const,
};

// ============================================================================
// API client
// ============================================================================

export const bomApi = {
  /** GET /api/boms?parentType=&parentId= */
  listByParent: (parentType: "Quote" | "Job", parentId: number) =>
    apiFetch<BomListItem[]>(`/api/boms?parentType=${parentType}&parentId=${parentId}`),

  /** GET /api/boms/{id} */
  get: (bomId: number) => apiFetch<BomDetail>(`/api/boms/${bomId}`),

  /** GET /api/boms/admin?status=&q=&page=&pageSize= */
  listAll: (q: { status?: string; q?: string; page?: number; pageSize?: number }) => {
    const p = new URLSearchParams();
    p.set("page", String(q.page ?? 0));
    p.set("pageSize", String(q.pageSize ?? 50));
    if (q.status) p.set("status", q.status);
    if (q.q) p.set("q", q.q);
    return apiFetch<BomAdminListPage>(`/api/boms/admin?${p.toString()}`);
  },

  /** GET /api/boms/{id}/events */
  events: (bomId: number) =>
    apiFetch<{ items: BomEvent[]; total: number }>(`/api/boms/${bomId}/events`),

  /** PATCH /api/boms/{id} */
  patch: (bomId: number, body: Record<string, unknown>, rowVersion: string | null) =>
    apiFetch<BomDetail>(`/api/boms/${bomId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      headers: rowVersion ? { "If-Match": rowVersion } : undefined,
    }),

  /** POST /api/boms/{id}/items */
  addItem: (bomId: number, body: Record<string, unknown>) =>
    apiFetch<BomLineItem>(`/api/boms/${bomId}/items`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** PATCH /api/boms/{id}/items/{itemId} */
  patchItem: (
    bomId: number,
    itemId: number,
    body: Record<string, unknown>,
    rowVersion: string | null,
  ) =>
    apiFetch<BomLineItem>(`/api/boms/${bomId}/items/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      headers: rowVersion ? { "If-Match": rowVersion } : undefined,
    }),

  /** DELETE /api/boms/{id}/items/{itemId} */
  deleteItem: (bomId: number, itemId: number, rowVersion: string | null) =>
    apiFetch<void>(`/api/boms/${bomId}/items/${itemId}`, {
      method: "DELETE",
      headers: rowVersion ? { "If-Match": rowVersion } : undefined,
    }),

  /** POST /api/boms/{id}/items/reorder */
  reorder: (bomId: number, order: { bomLineItemId: number; sortOrder: number }[]) =>
    apiFetch<void>(`/api/boms/${bomId}/items/reorder`, {
      method: "POST",
      body: JSON.stringify({ order }),
    }),

  /** POST /api/boms/{id}/apply-template */
  applyTemplate: (bomId: number, bomTemplateId: number, overwriteEdits: boolean) =>
    apiFetch<BomDetail>(`/api/boms/${bomId}/apply-template`, {
      method: "POST",
      body: JSON.stringify({ bomTemplateId, overwriteEdits }),
    }),

  /** POST /api/boms/{id}/price */
  price: (bomId: number, body: PriceBomBody) =>
    apiFetch<PricingResult>(`/api/boms/${bomId}/price`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

// ============================================================================
// React Query hooks (new)
// ============================================================================

export function useBomsByParent(parentType: "Quote" | "Job", parentId: number) {
  return useQuery({
    queryKey: bomKeys.byParent(parentType, parentId),
    queryFn: () => bomApi.listByParent(parentType, parentId),
    enabled: !!parentId,
  });
}

export function useBom(bomId: number | null) {
  return useQuery({
    queryKey: bomKeys.detail(bomId ?? 0),
    queryFn: () => bomApi.get(bomId!),
    enabled: !!bomId,
  });
}

// ============================================================================
// Legacy hook (kept for BomPreviewTable — Plan I stub)
// ============================================================================

/**
 * Returns a preview of the BOM attached to a job. Currently returns an empty
 * preview because no "list BOMs by parent" endpoint existed in BOM Plan I.
 * BomPreviewTable still uses this hook; it will be superseded by useBomsByParent
 * in a later task.
 *
 * The `_options` argument is reserved for `{ limit }` once wiring lands.
 */
export function useJobBomPreview(
  jobId: number,
  _options: { limit?: number } = {},
) {
  return useQuery<BomPreviewDto, ApiError>({
    queryKey: bomKeys.jobPreview(jobId),
    queryFn: () => Promise.resolve<BomPreviewDto>({ bomId: null, items: [] }),
    enabled: !!jobId,
    staleTime: 30_000,
  });
}
